/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { EFFECTIVENESS_ENTITY_RUBRICS } from '../../../common/constants';

export const RUBRICS_ROUTE = 'rubrics';
export const RUBRICS_TAB_PATH = `/${RUBRICS_ROUTE}`;
export const RUBRICS_TAB_TITLE = 'rubrics';
export const RUBRICS_GRAPH_FIELD_NAME = 'graphId';
export const RUBRICS_ENTITY_TYPE = EFFECTIVENESS_ENTITY_RUBRICS;
export const RUBRICS_STATUS_DAILY = 'daily';
export const RUBRICS_STATUS_ONE_TIME = 'oneTime';
export const RUBRICS_STATUS_MANUAL = 'manual';
export const RUBRICS_STATUS_FILTER_ALL = 'all';
export const RUBRICS_STATUS_FILTERS = [
  RUBRICS_STATUS_FILTER_ALL,
  RUBRICS_STATUS_DAILY,
  RUBRICS_STATUS_ONE_TIME,
  RUBRICS_STATUS_MANUAL
];
export const RUBRICS_SORT_NEXT_RUN = 'nextRun';
export const RUBRICS_SORT_NAME = 'name';
export const RUBRICS_SORTS = [RUBRICS_SORT_NEXT_RUN, RUBRICS_SORT_NAME];
export const RUBRICS_SAMPLE_PERCENT_MIN = 1;
export const RUBRICS_SAMPLE_PERCENT_MAX = 100;
export const RUBRICS_DEFAULT_SAMPLE_PERCENT = 10;
// No `g` flag on purpose: a shared regex with one would carry lastIndex between call sites.
export const RUBRICS_SAMPLE_PERCENT_PATTERN = /^\d{1,3}$/;
export const RUBRICS_NEW_ROUTE = 'new';
export const RUBRICS_EDIT_ROUTE = 'edit';
export const RUBRICS_SCHEDULE_ROUTE = 'schedule';
export const RUBRICS_VIEW_ROUTE = 'view';
export const RUBRICS_REPORT_ROUTE = 'report';
export const RUBRICS_RESULT_ROUTE = 'result';
export const RUBRICS_EVAL_REPORT_CONFIG_KEY = 'evalReportSearchConfig';
export const RUBRICS_MODE_NEW = 'new';
export const RUBRICS_MODE_EDIT = 'edit';
export const RUBRICS_MODE_SCHEDULE = 'schedule';
export const RUBRICS_MODE_VIEW = 'view';
export const RUBRICS_STEP_BASICS = 'basics';
export const RUBRICS_STEP_RUBRIC = 'rubric';
export const RUBRICS_STEP_SCHEDULER = 'scheduler';
export const RUBRICS_STEPS = [RUBRICS_STEP_BASICS, RUBRICS_STEP_RUBRIC, RUBRICS_STEP_SCHEDULER];

// Label keys per value, so no key is assembled at runtime: every one stays greppable and a missing
// entry shows up as the raw key rather than failing silently.
// The mode each form route stands for, keyed by its own last path segment.
export const RUBRICS_MODE_BY_SEGMENT = {
  [RUBRICS_EDIT_ROUTE]: RUBRICS_MODE_EDIT,
  [RUBRICS_SCHEDULE_ROUTE]: RUBRICS_MODE_SCHEDULE,
  [RUBRICS_VIEW_ROUTE]: RUBRICS_MODE_VIEW
};

export const RUBRICS_STATUS_LABEL_KEYS = {
  [RUBRICS_STATUS_FILTER_ALL]: 'rubrics.FILTER_ALL',
  [RUBRICS_STATUS_DAILY]: 'rubrics.DAILY',
  [RUBRICS_STATUS_ONE_TIME]: 'rubrics.ONE_TIME',
  [RUBRICS_STATUS_MANUAL]: 'rubrics.MANUAL'
};

export const RUBRICS_SORT_LABEL_KEYS = {
  [RUBRICS_SORT_NEXT_RUN]: 'rubrics.NEXT_RUN',
  [RUBRICS_SORT_NAME]: 'rubrics.SORT_NAME'
};

export const RUBRICS_STEP_TEXT_KEYS = {
  [RUBRICS_STEP_BASICS]: { title: 'rubrics.STEP_BASICS_TITLE', note: 'rubrics.STEP_BASICS_NOTE' },
  [RUBRICS_STEP_RUBRIC]: { title: 'rubrics.RUBRIC', note: 'rubrics.STEP_RUBRIC_NOTE' },
  [RUBRICS_STEP_SCHEDULER]: { title: 'rubrics.STEP_SCHEDULER_TITLE', note: 'rubrics.STEP_SCHEDULER_NOTE' }
};
export const RUBRICS_CADENCE_ONE_TIME = 'oneTime';
export const RUBRICS_CADENCE_DAILY = 'daily';
export const RUBRICS_EVAL_SCHEDULE_DAILY = 'D';
export const RUBRICS_STREAM_STATUS_STREAMING = 'streaming';
export const RUBRICS_STREAM_TYPE_CHUNK = 'chunk';
export const RUBRICS_RUBRIC_MAX_LENGTH = 20000;
export const RUBRICS_DATE_FORMAT = 'YYYY-MM-DD';
export const RUBRICS_TIME_FORMAT = 'HH:mm';
export const RUBRICS_NEXT_RUN_FORMAT = 'MMM D, YYYY HH:mm';
export const RUBRICS_CUSTOM_EVAL_REQUESTED_FORMAT = RUBRICS_NEXT_RUN_FORMAT;
export const RUBRICS_SCHEDULER_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export const RUBRICS_COLUMN_RUBRIC_NAME = 'rubricName';
export const RUBRICS_COLUMN_STATUS = 'status';
export const RUBRICS_COLUMN_SCORING_MODE = 'scoringMode';
export const RUBRICS_COLUMN_SCHEDULE = 'schedule';
export const RUBRICS_COLUMN_SAMPLE = 'sample';
export const RUBRICS_COLUMN_MODIFIED_BY = 'modifiedBy';
export const RUBRICS_DEFAULT_TABLE_COLUMNS = [
  { id: RUBRICS_COLUMN_RUBRIC_NAME, label: 'Rubric' },
  { id: RUBRICS_COLUMN_STATUS, label: 'Status' },
  { id: RUBRICS_COLUMN_SCORING_MODE, label: 'Scoring mode' },
  { id: RUBRICS_COLUMN_SCHEDULE, label: 'Schedule' },
  { id: RUBRICS_COLUMN_SAMPLE, label: 'Sample' },
  { id: RUBRICS_COLUMN_MODIFIED_BY, label: 'Modified by' }
];
